<template>
  <div>
    <v-dialog v-model="visible" max-width="800" persistent>
      <v-card class="transport-form-card">
        <v-card-title class="primary white--text py-4">
          <v-icon left color="white">mdi-truck</v-icon>
          Detalles del Transporte
          <v-spacer></v-spacer>
          <v-btn icon dark @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-stepper v-if="!postCreateDeliverMode" v-model="currentStep" class="elevation-0">
          <stepper-header :current-step="currentStep" :is-last-movement-entry="isLastMovementEntry" :is-assignment="isAssignment" :is-movement="isMovement" :is-recepcionar="isRecepcionar"
            :show-documentation="status.status === 'created' || isRecepcionar" :show-destination-steps="true" />

          <v-stepper-items>
            <!-- Paso 1: Empresa de destino (dealer del grupo) -->
            <v-stepper-content step="1">
              <v-card flat class="mb-4">
                <v-card-text>
                  <div class="text-center py-4">
                    <h3 class="headline mb-2">Empresa de destino</h3>
                    <p class="subtitle-1 grey--text mb-5">Seleccione la empresa (dealer) a la que se recibirá el vehículo. Solo se muestran los dealers del grupo al que pertenece.</p>
                    <v-row justify="center">
                      <v-col cols="12" md="8">
                        <v-select
                          v-model="destinationDealerId"
                          :items="destinationDealers"
                          item-text="name"
                          item-value="id"
                          label="Empresa de destino"
                          :loading="groupDealersLoading"
                          :disabled="groupDealersLoading"
                          outlined
                          dense
                          no-data-text="No hay dealers disponibles en su grupo"
                        ></v-select>
                      </v-col>
                    </v-row>
                    <v-btn color="primary" :disabled="!destinationDealerId" @click="currentStep = 2">
                      Siguiente
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-stepper-content>

            <!-- Paso 2: Ubicación de destino -->
            <v-stepper-content step="2">
              <v-card flat class="mb-4">
                <v-card-text>
                  <div class="text-center py-4">
                    <h3 class="headline mb-2">Ubicación de destino</h3>
                    <p class="subtitle-1 grey--text mb-5">Seleccione la ubicación de la empresa de destino donde se recibirá el vehículo.</p>
                    <v-row justify="center">
                      <v-col cols="12" md="8">
                        <v-select
                          v-model="destinationLocationId"
                          :items="destinationLocations"
                          item-text="displayName"
                          item-value="id"
                          label="Ubicación de destino"
                          :loading="destinationLocationsLoading"
                          :disabled="destinationLocationsLoading || destinationLocations.length === 0"
                          outlined
                          dense
                          no-data-text="La empresa seleccionada no tiene ubicaciones"
                        ></v-select>
                      </v-col>
                    </v-row>
                    <v-btn text @click="currentStep = 1">Anterior</v-btn>
                    <v-btn color="primary" class="ml-2" :disabled="!destinationLocationId" @click="currentStep = 3">
                      Siguiente
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-stepper-content>

            <!-- Paso 3: Transporte -->
            <transport-method-step :transport-method="transportMethod" :movement-type="movementType" :step-number="3"
              :selected-driver="selectedDriver" :selected-grua="selectedGrua" :datetime="datetime" :end-date="end_date"
              :initial-location="initialLocation" :final-location="destinationLocationId || finalLocation" :location-options="transportLocationOptions"
              :show-destination-location="true"
              :available-transport-options="availableTransportOptions" :validations="validations" :errors="errors"
              :current-dealer-id="currentDealerId" :last-movement="lastMovement" :is-processing="isProcessing"
              :is-completing-movement="isCompletingMovement"
              :is-new-move="isNewMove" :current-user="currentUser" :status="status" :isAssignment="isAssignment" :isMovement="isMovement" :isRecepcionar="isRecepcionar"
              :is-last-movement-entry="isLastMovementEntry" :loading="loading" :is-recepcionar="isRecepcionar"
              :additional-info.sync="additionalInfo"
              @update:additionalInfo="additionalInfo = $event"
              @update:transportMethod="transportMethod = $event" @update:movementType="movementType = $event"
              @update:selectedDriver="selectedDriver = $event" @update:selectedGrua="selectedGrua = $event"
              @update:datetime="datetime = $event" @update:endDate="end_date = $event"
              @update:initialLocation="handleInitialLocationUpdate" @update:finalLocation="handleFinalLocationUpdate"
              @driver-created="handleDriverCreated" @grua-created="handleGruaCreated" @next-step="nextStep"
              @submit="submit" @submit-deliver="handleSubmitDeliver" @movimiento-entregado="handleMovimientoEntregado" />
          </v-stepper-items>
        </v-stepper>
        <v-dialog v-model="showMobileConfirmDialog" max-width="490" class="d-md-none">
          <v-card class="rounded-xl pa-4 text-center">
            <v-card-text class="text-body-1 font-weight-medium">
              ¿Estás seguro que quieres proceder<br />
              con la {{ (status.movements && status.movements.length > 0) ? 'entrega' : 'recepción' }} del vehículo?
            </v-card-text>

            <v-card-actions class="d-flex flex-column mt-4">
              <v-btn color="green lighten-1" dark block class="mb-3 text-caption font-weight-bold"
                @click="handleMobileConfirmation(true)">
                Sí, quiero {{ (status.movements && status.movements.length > 0) ? 'entregarlo' : 'recibirlo' }}
              </v-btn>

              <v-btn outlined color="red" block class="text-caption font-weight-bold"
                @click="handleMobileConfirmation(false)">
                No, déjame consultarlo
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <movement-processing-view v-if="isProcessing && lastMovement && lastMovement.type !== 'entry' && status.status !== 'free' && ((!isAssignment && !isMovement) || postCreateDeliverMode)"
          :is-processing="isProcessing" :last-movement="lastMovement" :is-movement-completed="isMovementCompleted"
          :status="status" :transport-method.sync="transportMethod" :selected-driver.sync="selectedDriver"
          :selected-second-driver.sync="selectedSecondDriver" :selected-grua.sync="selectedGrua" :datetime.sync="datetime" :end-date.sync="end_date"
          :reception-datetime.sync="reception_datetime" :initial-location.sync="initialLocation"
          :final-location.sync="finalLocation" :initial-km.sync="initialKm" :final-km.sync="finalKm"
          :driver-type.sync="driverType" :vehicle-condition.sync="vehicleCondition" :damage-notes.sync="damageNotes"
          :new-damage-images.sync="newDamageImages" :new-image-previews.sync="newImagePreviews"
          :images-to-remove.sync="imagesToRemove" :emergency-light.sync="emergencyLight"
          :emergency-triangle.sync="emergencyTriangle" :emergency-vest.sync="emergencyVest"
          :documentation.sync="documentation" :charging-cable.sync="chargingCable"
          :customer-signature-data-u-r-l.sync="customerSignatureDataURL"
          :customer-signature-return-data-u-r-l.sync="customerSignatureReturnDataURL"
          :show-customer-signature.sync="showCustomerSignature"
          :show-customer-signature-return.sync="showCustomerSignatureReturn"
          :has-customer-signature="hasCustomerSignatureData"
          :has-customer-signature-return="hasCustomerSignatureReturnData" :validations="validations" :errors="errors"
          :loading="loading" :location-options="locationOptions"
          :available-transport-options="availableTransportOptions" :vehicle-type-options="vehicleTypeOptions"
          :current-dealer-id="currentDealerId" :current-user="currentUser" :is-disabled="isDisabled"
          :additional-info-delivery.sync="additionalInfoDelivery"
          :additional-info-reception.sync="additionalInfoReception"
          :show-additional-info.sync="showAdditionalInfo"
          :new-additional-images.sync="newAdditionalImages"
          :new-additional-image-previews.sync="newAdditionalImagePreviews"
          :saved-additional-images.sync="savedAdditionalImages"
          :additional-images-to-remove.sync="additionalImagesToRemove"
          :new-additional-docs.sync="newAdditionalDocs"
          :saved-additional-docs.sync="savedAdditionalDocs"
          :additional-docs-to-remove.sync="additionalDocsToRemove"
          :new-damage-docs.sync="newDamageDocs"
          :saved-damage-docs.sync="savedDamageDocs"
          :damage-docs-to-remove.sync="damageDocsToRemove"
          :delivery-date.sync="delivery_date"
          :selected-equipments.sync="selectedEquipments"
          :selected-todos.sync="selectedTodos"
          :franchise.sync="franchise"
          :km-limit.sync="km_limit"
          :accept-treatment="accept_treatment"
          @update:acceptTreatment="accept_treatment = $event"
          :accept-treatment-driver-2.sync="acceptTreatmentDriver2"
          :driver2-signature-data-u-r-l.sync="driver2SignatureDataURL"
          :show-driver2-signature.sync="showDriver2Signature"
          :has-driver2-signature="hasDriver2SignatureData"
          :driver2-signature-return-data-u-r-l.sync="driver2SignatureReturnDataURL"
          :show-driver2-signature-return.sync="showDriver2SignatureReturn"
          :has-driver2-signature-return="hasDriver2SignatureReturnData"
          :dealer-franchises="dealerFranchises"
          :dealer-km-limits="dealerKmLimits"
          :default-franchise-index="defaultFranchiseIndex"
          :default-km-limit-index="defaultKmLimitIndex"
          @update:additionalInfoDelivery="additionalInfoDelivery = $event"

          @additional-image-removed="handleAdditionalImageRemoved"
          @additional-doc-removed="handleAdditionalDocRemoved"
          @damage-doc-removed="handleDamageDocRemoved"
          @complete-movement="submit"
          @close="close" />

        <v-dialog v-model="createDriverDialog" max-width="500">
          <driver-form @save="handleDriverCreated" @cancel="createDriverDialog = false" :created="true" />
        </v-dialog>
        <v-dialog v-model="createGruaDialog" max-width="600">
          <grua-form @save="handleGruaCreated" @cancel="createGruaDialog = false" :created="true" />
        </v-dialog>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import DriverService from '../services/DriverService'
import LocationService from '../services/LocationService'
import { locationDisplayName } from '@/utils'
import GrupoService from '../services/GrupoService'
import MovimientoService from '../services/MovimientoService'
import DriverForm from '../views/driver/DriverForm'
import VehiculoService from '../services/VehiculoService'
import EquipmentService from '../services/EquipmentService'
import { mapGetters } from 'vuex'
// import CustomDatePicker from './CustomDatePicker.vue'
import MobileDetect from 'mobile-detect'
import GruaService from '../services/GruaService'
import UserService from '../services/UserService'
import GruaForm from '../views/grua/GruaForm'
import SignaturePad from 'signature_pad'

// Nuevos componentes modulares
import StepperHeader from './StepperHeader.vue'
import TransportMethodStep from './TransportMethodStep.vue'
import MovementProcessingView from './MovementProcessingView.vue'
// import DriverSelector from './DriverSelector.vue'
// import GruaSelector from './GruaSelector.vue'
// import ImageManager from './ImageManager.vue'

import errorHandler from '../mixins/errorHandler'

export default {
  components: {
    DriverForm,
    GruaForm,
    // CustomDatePicker,
    // Nuevos componentes modulares
    StepperHeader,
    TransportMethodStep,
    MovementProcessingView
    // DriverSelector,
    // GruaSelector,
    // ImageManager
  },
  props: {
    visible: Boolean,
    status: {
      type: Object,
      required: true
    },
    isAssignment: {
      type: Boolean,
      default: false
    },
    isMovement: {
      type: Boolean,
      default: false
    },
    isRecepcionar: {
      type: Boolean,
      default: true
    },
    noRedirect: {
      type: Boolean,
      default: false
    }
  },
  mixins: [errorHandler],
  data () {
    return {
      currentStep: 1,
      loading: false,
      isSubmitting: false,
      lastSubmitTime: 0,
      isDelivering: false,
      postCreateDeliverMode: false,
      // Documentación del vehículo (paso 4, solo 1ª recepción, opcional)
      policyNumber: null,
      vehicleDocFiles: {
        technical_sheet_front_image: null,
        technical_sheet_back_image: null,
        circulation_permit_front_image: null,
        circulation_permit_back_image: null
      },
      transportMethod: '',
      selectedDriver: null,
      selectedSecondDriver: null,
      selectedGrua: null,
      driverState: {
        list: [],
        loading: false,
        search: ''
      },
      gruaState: {
        list: [],
        loading: false,
        search: ''
      },
      createGruaDialog: false,
      // Nuevas propiedades para manejar imágenes
      newDamageImages: [], // Para nuevas imágenes a subir
      newImagePreviews: [], // URLs de vista previa para nuevas imágenes
      tempDamageImages: [], // Variable temporal para el v-file-input
      imageDialog: false, // Control del diálogo de vista previa
      selectedImageUrl: '', // URL de la imagen seleccionada para vista previa
      imagesToRemove: [], // IDs de imágenes a eliminar
      
      // Propiedades para información adicional
      additionalInfo: '', // Para TransportMethodStep (creación inicial)
      additionalInfoReception: '', // Para VehicleConditionStep (información de recepción)
      additionalInfoDelivery: '', // Para MovementProcessingView (información de entrega)
      showAdditionalInfo: false,
      newAdditionalImages: [],
      newAdditionalImagePreviews: [],
      savedAdditionalImages: [],
      additionalImagesToRemove: [],
      newAdditionalDocs: [],
      savedAdditionalDocs: [],
      additionalDocsToRemove: [],
      newDamageDocs: [],
      savedDamageDocs: [],
      damageDocsToRemove: [],
      dataLoaded: false, // Bandera para evitar cargas múltiples
      dealerComodatoConfig: null, // Config de comodato cargada fresca desde getMe
      lastMovement: {},
      movementType: '',
      end_date: null,
      md: new MobileDetect(navigator.userAgent),
      showMobileConfirmDialog: false,
      mobileConfirmAction: '',
      isSecure: false,
      initialLocation: '',
      initialKm: '',
      finalKm: '',
      datetime: null,
      reception_datetime: Math.floor(Date.now() / 1000),
      finalLocation: '',
      driverType: '',
      vehicleCondition: '',
      damageNotes: '',
      transportCompany: null,
  emergencyLight: false,
  emergencyTriangle: false,
  emergencyVest: false,
      documentation: false,
  chargingCable: false,
      selectedEquipments: {}, // Objeto para almacenar equipamientos seleccionados dinámicamente
      selectedTodos: [], // TO-DOs seleccionados para asignar al vehículo en la recepción (opcional)
      originalTodoIds: [], // TO-DOs que ya tenía asignados el vehículo (para detectar cambios)
      // Signature pad properties
      signaturePad: null,
      signatureDataURL: '',
      showSignature: false,
      // Customer signature properties for delivery (entrega)
      customerSignaturePad: null,
      customerSignatureDataURL: '',
      showCustomerSignature: false,
      // Customer signature properties for return (devolución)
      customerSignatureReturnPad: null,
      customerSignatureReturnDataURL: '',
      showCustomerSignatureReturn: false,
      createDriverDialog: false,
      showNoDriversMessage: false,
      // Variables para manejar "YO MISMO"
      userHasDriver: false,
      checkingUserDriver: false,
      transportOptions: [],
      // vehicleTypeOptions: [
      //   { text: 'MATRICULADO', value: 'MATRICULADO', color: 'success' },
      //   { text: 'PLACAS ROJAS', value: 'PLACAS ROJAS', color: 'warning' },
      //   { text: 'NO MATRICULADO', value: 'NO MATRICULADO', color: 'error' }
      // ],
      autocompleteKey: 0,
      locationOptions: [],
      // Empresa de destino (recepción grupal)
      groupDealersLoading: false,
      destinationDealers: [],
      destinationDealerId: null,
      destinationLocationsLoading: false,
      destinationLocations: [],
      destinationLocationId: null,
      delivery_date: null,
      franchise: null, // Franquicia seleccionada para cesiones
      km_limit: null, // Límite de KM seleccionado para cesiones
      accept_treatment: false, // Acepta tratamiento
      acceptTreatmentDriver2: false, // Acepta tratamiento conductor 2
      driver2SignatureDataURL: '',
      showDriver2Signature: false,
      driver2SignatureReturnDataURL: '',
      showDriver2SignatureReturn: false,
      // validaciones
      validations: {
        isValid: true,
        vehicle_id: [
          (v) => !!v || 'Debe proporcionar un ID de vehículo.'
        ],
        driver_id: [],
        datetime: [
          (v) => !!v || 'Debe proporcionar una fecha y hora.'
        ],
        source_location_id: [
          (v) => !!v || 'Debe proporcionar una ubicación de origen.'
        ],
        destination_location_id: [
          (v) => !!v || 'Debe proporcionar una ubicación de destino.'
        ],
        transportation: [
          (v) => !!v || 'Debe proporcionar un modo de transporte.'
        ],
        responsible: [],
        observations: [],
        initial_km: [
          (v) => !!v || 'Debe proporcionar el kilometraje inicial.'
        ],
        final_km: [
        (v) => !v || !this.initialKm || parseInt(v) >= parseInt(this.initialKm) || 'El kilometraje final debe ser mayor que el inicial.'
      ],
        vehicle_status: [
          (v) => !!v || 'Debe proporcionar el estado del vehículo.'
        ],
        registered: [
          (v) => !!v || 'Debe proporcionar si está matriculado.'
        ],
        _images: [
          (v) => v.length <= 5 || 'No puede subir más de 5 imágenes.'
        ]
      },

      // errores
      errors: {
        vehicle_id: [],
        driver_id: [],
        datetime: [],
        source_location_id: [],
        destination_location_id: [],
        transportation: [],
        responsible: [],
        status: [],
        observations: [],
        initial_km: [],
        final_km: [],
        vehicle_status: [],
        registered: [],
        _images: []
      }
    }
  },
  computed: {
    ...mapGetters({
      currentUser: 'CURRENT_USER'
    }),
    dealerFranchises() {
      const franchises = this.dealerComodatoConfig?.franchises || this.currentUser?.dealer?.comodato_config?.franchises || []
      return franchises
    },
    dealerKmLimits() {
      const limits = this.dealerComodatoConfig?.km_limits || this.currentUser?.dealer?.comodato_config?.km_limits || []
      return limits
    },
    defaultFranchiseIndex() {
      const index = this.dealerComodatoConfig?.default_franchise_index ?? this.currentUser?.dealer?.comodato_config?.default_franchise_index
      const parsedIndex = index !== undefined && index !== null ? parseInt(index) : null
      return parsedIndex
    },
    defaultKmLimitIndex() {
      const index = this.dealerComodatoConfig?.default_km_limit_index ?? this.currentUser?.dealer?.comodato_config?.default_km_limit_index
      const parsedIndex = index !== undefined && index !== null ? parseInt(index) : null
      return parsedIndex
    },
     vehicleConditionComputed() {
      if (!this.status || !this.status.condition) return 'nuevo'
      const cond = this.status.condition.toLowerCase()
      if (cond === 'new') return 'nuevo'
      if (cond === 'used') return 'usado'
      return cond
    },
    vehicleType() {
      if (!this.status || !this.status.fuel) return 'gasolina_diesel_hibrido'
      const fuelRaw = String(this.status.fuel)
      const fuel = fuelRaw
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()

      if (['gasolina', 'diesel', 'hibrido'].includes(fuel)) return 'gasolina_diesel_hibrido'
      if (fuel === 'hibrido enchufable') return 'hibrido_enchufable'
      if (fuel === 'electrico') return 'electrico'
      return 'gasolina_diesel_hibrido'
    },
    isCollaborator() {
      return this.$store.getters.HAS_ROLE(['colaborator'])
    },
    currentDealerId() {
      // Buscar dealer_id en los datos del vehículo (status)
      if (this.status && this.status.dealer_id) {
        return this.status.dealer_id
      }
      
      // Si el vehículo tiene un objeto dealer
      if (this.status && this.status.dealer && this.status.dealer.id) {
        return this.status.dealer.id
      }
      
      return null
    },
    transportLocationOptions() {
      // Combina las ubicaciones generales con las del dealer de destino seleccionado
      const combined = [...this.locationOptions]
      this.destinationLocations.forEach(loc => {
        if (!combined.some(x => x.id === loc.id)) {
          combined.push(loc)
        }
      })
      return combined
    },
    vehicleTypeOptions() {
    // Base de las opciones
    const options = [
      { text: 'MATRICULADO', value: 'MATRICULADO', color: 'success', disabled: !this.hasPlateNumber },
      { text: 'PLACAS ROJAS', value: 'PLACAS ROJAS', color: 'warning', disabled: this.hasPlateNumber },
      { text: 'NO MATRICULADO', value: 'NO MATRICULADO', color: 'error', disabled: this.hasPlateNumber }
    ]

    return options
  },
  isDisabled() {
    // Basic required fields check
    if (!this.initialKm || !this.driverType || !this.datetime || (!this.finalKm && this.lastMovement.status === 'processing')) {
      return true
    }
    
    // Special check for assignment type
    if (this.movementType === 'assignment' || 
        (this.lastMovement && this.lastMovement.type === 'assignment')) {
      // Check if end date exists
      if (!this.end_date) {
        return true
      }
      
      // Check for return signature if it's in processing state
      if (this.lastMovement && 
          this.lastMovement.type === 'assignment' && 
          this.lastMovement.status === 'reported' && 
          !this.customerSignatureDataURL) {
        return true
      }
      if (this.lastMovement && 
          this.lastMovement.type === 'assignment' && 
          this.lastMovement.status === 'processing' && 
          !this.customerSignatureReturnDataURL) {
        return true
      }
    }
    
    return false
  },
  
    hasPlateNumber() {
    return !!this.status.plate_number
  },
  isLastMovementEntry () {
    if (this.status && this.status.status === 'free') {
      return false 
    }
    return (this.status.movements &&
          this.status.movements.length > 0 &&
          this.lastMovement && this.lastMovement.type === 'entry') || (this.lastMovement && this.lastMovement.type === 'exit' && this.lastMovement.status === 'completed') || (this.lastMovement && this.lastMovement.type === 'assignment' && this.lastMovement.status === 'completed')
  },
    availableTransportOptions () {
      if (this.movementType === 'assignment') {
        return ['YO MISMO', 'CLIENTE']
      }
      const baseOptions = ['DRIVER', 'GRUA']
      // If vehicle status is not 'created', include 'YO MISMO'
      if (this.status.status !== 'created' && !this.isRecepcionar) {
        baseOptions.push('YO MISMO')
      }
      // Include 'CLIENTE' only if last movement is not 'entry'
      // and not (move with completed status)
      const lastMove = this.lastMovement
      const isLastMoveEntryOrCompleted =
        lastMove && (
          lastMove.type === 'entry' ||
          (lastMove.type === 'exit' && lastMove.status === 'completed') ||
          (lastMove.type === 'assignment' && lastMove.status === 'completed') || this.status.movements.length > 1
        )
      if (!isLastMoveEntryOrCompleted || this.isRecepcionar) {
        baseOptions.push('CLIENTE')
      }
      return baseOptions
    },
    showAddGruaButton () {
      if (!this.gruaState.search) {
        return false
      }
      const gruaExists = this.gruaState.list.some(grua =>
        grua.company.toLowerCase().includes(this.gruaState.search.toLowerCase())
      )
      return !gruaExists
    },
    isProcessing () {
      if (this.status.movements && this.status.movements.length > 0) {
        const sortedMovements = [...this.status.movements].sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        )
        return sortedMovements[0].status === 'reported' || sortedMovements[0].status === 'processing'
      }
      return false
    },
    // Determina si estamos COMPLETANDO un movimiento existente (no creando uno nuevo)
    isCompletingMovement () {
      return this.isProcessing && 
             this.lastMovement && 
             this.lastMovement.type !== 'entry' && 
             this.status.status !== 'free' && 
             (!this.isAssignment || this.postCreateDeliverMode) && 
             (!this.isMovement || this.postCreateDeliverMode)
    },
    isNewMove () {
      if (this.status && this.status.status === 'free') {
        return false
      }
      if (this.status.movements && this.status.movements.length > 0) {
        const sortedMovements = [...this.status.movements].sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        )
        
        // No es un movimiento nuevo si el último movimiento está en estado 'reported' o 'processing'
        const lastMovement = sortedMovements[0]
        if (lastMovement.status === 'reported' || lastMovement.status === 'processing') {
          return false
        }
        
        return sortedMovements[0].type === 'entry' || sortedMovements[0].type === 'exit' || (sortedMovements[0].status === 'completed' && sortedMovements[0].type === 'assignment') || (sortedMovements.length > 1)
      } else if (!this.status.movements || this.status.movements.length === 0) {
        return false
      }
      return false
    },
    isFirma () {
      if (this.lastMovement) {
        return this.lastMovement.type === 'assignment' && 
               (this.lastMovement.status === 'processing' || this.lastMovement.status === 'reported') &&
               this.lastMovement.transportation === 'CLIENTE'
      } else if (!this.this.lastMovement) {
        return false
      }
      return false
    },
    isMovementCompleted () {
      return this.lastMovement && this.lastMovement.status === 'completed' && this.lastMovement.type === 'exit'
    },
    selectedDriverText () {
      return this.selectedDriver ? `${this.selectedDriver.name} ${this.selectedDriver.lastname}` : 'No seleccionado'
    },
    vehicleConditionText () {
      return this.vehicleCondition === 'good' ? 'BUENO' : 'HAY DAÑOS'
    },
    // Verifica si mostrar el botón de agregar conductor para "YO MISMO"
    showAddDriverButton () {
      // Solo mostrar si estamos en un movimiento con estado "reported" y transporte "YO MISMO"
      if (this.lastMovement && this.lastMovement.status === 'reported' && this.transportMethod === 'YO MISMO') {
        // Si no hemos verificado aún el driver del usuario, no mostrar
        
        if (this.checkingUserDriver) {
          return false
        }
        // Mostrar solo si el usuario no tiene driver asociado
        return !this.userHasDriver
      }
      
      // Lógica original para otros casos
      if (!this.driverState.search) {
        return false
      }
      // console.log(this.driverState.list.some(driver =>
      //   driver.document_number.toString().toLowerCase() === this.driverState.search.split('-').pop().trim().toLowerCase()
      // ))
      
      const driverExists = this.driverState.list.some(driver =>
        driver.document_number.toString().toLowerCase() === this.driverState.search.split('-').pop().trim().toLowerCase()
      )
      
      return !driverExists    
    },
    // Nuevas propiedades computadas para manejar imágenes
    savedImages () {
      if (!this.lastMovement || !this.lastMovement.imagesUrls) {
        return []
      }
      
      // Si es un array
      if (Array.isArray(this.lastMovement.imagesUrls)) {
        return this.lastMovement.imagesUrls.filter((_, index) => !this.imagesToRemove.includes(index))
      }
      
      // Si es un objeto con índices numéricos como claves
      if (typeof this.lastMovement.imagesUrls === 'object') {
        const imagesArray = Object.values(this.lastMovement.imagesUrls).filter(img => img && img.url)
        return imagesArray.filter((_, index) => !this.imagesToRemove.includes(index))
      }
      
      return []
    },
    savedImagesCount () {
      if (!this.lastMovement || !this.lastMovement.imagesUrls) {
        return 0
      }
      
      // Si es un array
      if (Array.isArray(this.lastMovement.imagesUrls)) {
        return this.lastMovement.imagesUrls.length - this.imagesToRemove.length
      }
      
      // Si es un objeto con índices numéricos como claves
      if (typeof this.lastMovement.imagesUrls === 'object') {
        const imagesArray = Object.values(this.lastMovement.imagesUrls).filter(img => img && img.url)
        return imagesArray.length - this.imagesToRemove.length
      }
      
      return 0
    },
    remainingImageSlots () {
      return Math.max(0, 4 - this.savedImagesCount - (this.newDamageImages ? this.newDamageImages.length : 0))
    },
    canAddMoreImages () {
      return this.remainingImageSlots > 0
    },
    hasSignature () {
      return this.signaturePad && !this.signaturePad.isEmpty()
    },
    // Customer signature computed properties
    shouldShowCustomerSignature () {
      const shouldShow = this.lastMovement && this.lastMovement.type === 'assignment' && this.lastMovement.status === 'reported'
      return shouldShow
    },
    shouldShowCustomerSignatureReturn () {
      const shouldShow = this.lastMovement && this.lastMovement.type === 'assignment' && this.lastMovement.status === 'processing'
      return shouldShow
    },
    hasCustomerSignature () {
      // Check if signature pad exists and has data (not if it has saved data)
      return this.customerSignaturePad && !this.customerSignaturePad.isEmpty()
    },
    hasCustomerSignatureReturn () {
      // Check if signature pad exists and has data (not if it has saved data)
      return this.customerSignatureReturnPad && !this.customerSignatureReturnPad.isEmpty()
    },
    hasCustomerSignatureData () {
      // Check if we have saved customer signature data
      return !!this.customerSignatureDataURL
    },
    hasCustomerSignatureReturnData () {
      // Check if we have saved customer signature return data
      return !!this.customerSignatureReturnDataURL
    },
    hasDriver2SignatureData () {
      return !!this.driver2SignatureDataURL
    },
    hasDriver2SignatureReturnData () {
      return !!this.driver2SignatureReturnDataURL
    }
  },
  async created () {
    // Cargar los dealers del grupo del usuario para el paso de empresa de destino
    this.loadGroupDealers()

    // Cargar datos frescos del usuario para obtener comodato_config actualizado
    try {
      const meResponse = await UserService.getMe()
      const meUser = meResponse?.data?.data || meResponse?.data
      if (meUser?.dealer?.comodato_config) {
        let config = meUser.dealer.comodato_config
        if (typeof config === 'string') {
          try { config = JSON.parse(config) } catch (e) { config = null }
        }
        this.dealerComodatoConfig = config
      }
    } catch (e) {
      console.warn('[TransportForm] Error al cargar getMe para comodato_config:', e)
    }

    // Si es modo cesión (assignment), limpiar todos los datos precargados y no tomar nada del último movimiento ni del status
    if (this.isAssignment || this.isMovement) {
      // this.lastMovement = {}
      this.transportMethod = ''
      this.selectedDriver = null
      this.selectedGrua = null
      this.driverType = ''
      this.initialLocation = ''
      this.finalLocation = ''
      this.initialKm = ''
      this.finalKm = ''
      this.datetime = null
      this.end_date = null
      this.vehicleCondition = ''
      this.damageNotes = ''
  this.emergencyLight = false
  this.emergencyTriangle = false
  this.emergencyVest = false
      this.documentation = false
  this.chargingCable = false
      this.transportCompany = null
      this.newDamageImages = []
      this.newImagePreviews = []
      this.tempDamageImages = []
      this.imagesToRemove = []
      this.additionalInfo = ''
      this.additionalInfoReception = ''
      this.additionalInfoDelivery = ''
      this.showAdditionalInfo = false
      this.newAdditionalImages = []
      this.newAdditionalImagePreviews = []
      this.savedAdditionalImages = []
      this.additionalImagesToRemove = []
      this.dataLoaded = false
      // Setear el tipo de movimiento por defecto
      if (this.isAssignment) {
        this.movementType = 'assignment'
      } else if (this.isMovement) {
        this.movementType = 'move'
      }
      // Cargar ubicaciones
      /* const locationParams = {}
      locationParams['expand'] = 'dealer'
      LocationService.get(locationParams).then(({ data }) => {
        let locations = data.data || []
        this.locationOptions = locations.map(location => {
          let displayName = location.name
          if (location.dealer && location.dealer.name) {
            displayName = `${location.name} (${location.dealer.name})`
          } else if (location.dealer && typeof location.dealer === 'string') {
            displayName = `${location.name} (${location.dealer})`
          } else if (location.dealer_name) {
            displayName = `${location.name} (${location.dealer_name})`
          }
          return {
            id: location.id,
            name: location.name,
            displayName: displayName
          }
        })
      }).catch(error => {
        this.handleApiError(error, 'Error al obtener ubicaciones')
      }) */
     await this.loadLocationOptions()
      // Cargar conductores
      DriverService.get({ expand: 'dealer' }).then(({ data }) => {
        let drivers = data.data || []
        this.driverState.list = drivers
      }).catch(error => {
        this.handleApiError(error, 'Error al obtener conductores')
      })
      // Cargar grúas
      GruaService.get({ expand: 'dealership' }).then(({ data }) => {
        let gruas = data.data || []
        this.gruaState.list = gruas
      }).catch(error => {
        this.handleApiError(error, 'Error al obtener grúas')
      })
      if (this.status.movements && this.status.movements.length > 0) {
        this.lastMovement = this.status.movements[this.status.movements.length - 1]
      }
      // Asignar initialLocation DESPUÉS de cargar ubicaciones (evitar race condition)
      if (this.status && this.status.location_id && this.status.status !== 'free') {
        this.initialLocation = this.status.location_id
      } else if (this.status && this.status.source_location_id && this.status.status !== 'free') {
        this.initialLocation = this.status.source_location_id
      }
      return
    }
    // Modo normal: precargar datos como antes
    // Configuración inicial basada en el estado
    if (this.status.status === 'created' || this.status.status === 'free') {
      this.datetime = Math.floor(Date.now() / 1000)
    }
    if (this.transportMethod === 'GRUA') {
      this.loadGruas()
    }
    DriverService.get({ expand: 'dealer' }).then(({ data }) => {
        let drivers = data.data || []
        this.driverState.list = drivers
    }).catch(error => {
        this.handleApiError(error, 'Error al obtener conductores')
      })
    if (this.status.movements && this.status.movements.length > 0) {
      this.lastMovement = this.status.movements[this.status.movements.length - 1]
    }
    // initialLocation se asignará DESPUÉS de loadLocationOptions() para evitar race condition
    if (this.isProcessing && this.lastMovement && this.lastMovement.type !== 'entry') {
      this.transportMethod = this.lastMovement.transportation
      
      // Solo establecer driverType desde lastMovement.registered si ya existe un valor
      // Si no existe (status === 'reported'), dejar que el componente auto-seleccione basándose en plate_number
      if (this.lastMovement.registered !== null && this.lastMovement.registered !== undefined) {
        this.driverType = this.lastMovement.registered === 1 ? 'MATRICULADO' : this.lastMovement.registered === 0 ? 'PLACAS ROJAS' : 'NO MATRICULADO'
      }
      
      // initialLocation/finalLocation se asignarán después de loadLocationOptions()

      this.finalLocation = this.lastMovement.destination_location_id
      this.initialKm = this.lastMovement.initial_km
      this.end_date = this.lastMovement.end_date
      this.delivery_date = this.lastMovement.delivery_date
      this.datetime = this.lastMovement.datetime
      // Los equipamientos ahora se cargan desde equipment_status en loadCompleteMovementData()
      this.accept_treatment = !!this.lastMovement.accept_treatment
      // Inicializar franchise y km_limit al abrir el formulario en processing
      if (this.lastMovement.type === 'assignment' && this.lastMovement.status === 'processing') {
        if (this.lastMovement.franchise !== undefined && this.lastMovement.franchise !== null) {
          const franchiseInt = parseInt(this.lastMovement.franchise, 10)
          this.franchise = Number.isNaN(franchiseInt) ? null : franchiseInt
        }
        if (this.lastMovement.km_limit !== undefined && this.lastMovement.km_limit !== null) {
          this.km_limit = this.lastMovement.km_limit
        }
      }
      // this.emergencyTriangle = !!this.lastMovement.emergency_triangle
      // this.emergencyLight = !!this.lastMovement.emergency_light
      // this.documentation = !!this.lastMovement.documentation
      // this.chargingCable = !!this.lastMovement.charging_cable
      this.vehicleCondition = this.lastMovement.vehicle_status
      
      // Solo establecer driverType desde lastMovement.registered si el valor es válido
      // Si es null/undefined (movimiento en 'reported'), dejar que auto-seleccione basándose en plate_number
      if (this.lastMovement.registered !== null && this.lastMovement.registered !== undefined) {
        this.driverType = this.lastMovement.registered === 1 ? 'MATRICULADO' : this.lastMovement.registered === 0 ? 'PLACAS ROJAS' : this.lastMovement.registered === 2 ? 'NO MATRICULADO' : ''
      }
      
      this.loadCompleteMovementData()
      // Solo cargar la grúa específica si estamos procesando un movimiento que usa GRUA
      if (this.transportMethod === 'GRUA' && this.lastMovement.crane_id) {
        this.loadGruas()
      }
      if (this.transportMethod === 'DRIVER' || this.transportMethod === 'CLIENTE') {
        if (this.lastMovement.status === 'processing') {
          const payload = { id: this.lastMovement.driver_id }
          DriverService.view(payload).then(({ data }) => {
            const selected = data.data
            this.driverState.list = selected ? [selected] : []
            this.selectedDriver = selected
            this.autocompleteKey++
          }).catch(error => {
            this.handleApiError(error, 'Error al obtener conductor específico')
          })
        } else {
          this.driverState.list = this.selectedDriver ? [this.selectedDriver] : []
        }
      }
      this.damageNotes = this.lastMovement.observations
      // Los equipamientos ahora se cargan desde equipment_status en loadCompleteMovementData()
      // this.emergency_light = !!this.lastMovement.emergency_light
      // this.emergency_triangle = !!this.lastMovement.emergency_triangle
      // this.emergency_vest = !!this.lastMovement.emergency_vest
      this.transportCompany = this.lastMovement.transport_company
    }
    // Cargar ubicaciones
    /* const locationParams = {}
    locationParams['expand'] = 'dealer'
    LocationService.get(locationParams).then(({ data }) => {
      let locations = data.data || []
      this.locationOptions = locations.map(location => {
        let displayName = location.name
        if (location.dealer && location.dealer.name) {
          displayName = `${location.name} (${location.dealer.name})`
        } else if (location.dealer && typeof location.dealer === 'string') {
          displayName = `${location.name} (${location.dealer})`
        } else if (location.dealer_name) {
          displayName = `${location.name} (${location.dealer_name})`
        }
        return {
          id: location.id,
          name: location.name,
          displayName: displayName
        }
      })

      if (this.status && this.status.source_location_id && this.status.status !== 'free') {
        this.initialLocation = this.status.source_location_id
      }
    }).catch(error => {
      this.handleApiError(error, 'Error al obtener ubicaciones')
    }) */
   await this.loadLocationOptions()
    // Asignar initialLocation/finalLocation DESPUÉS de cargar las ubicaciones
    if (this.isProcessing && this.lastMovement && this.lastMovement.type !== 'entry') {
      // En modo processing, usar el source del último movimiento
      this.initialLocation = this.status.status !== 'free' ? this.lastMovement.source_location_id : null
      this.finalLocation = this.lastMovement.destination_location_id
    } else if (this.status && this.status.location_id && this.status.status !== 'free') {
      // Para vehículos registered con ubicación conocida
      this.initialLocation = this.status.location_id
    } else if (this.status && this.status.source_location_id && this.status.status !== 'free') {
      this.initialLocation = this.status.source_location_id
    }
    if (!this.isProcessing) {
      const driverParams = {}
      driverParams['expand'] = 'dealer'
      if (this.currentDealerId) {
        driverParams['filter[dealer_id]'] = this.currentDealerId
      }
      DriverService.get(driverParams).then(({ data }) => {
        let drivers = data.data || []
        if (this.selectedDriver && !drivers.some(d => d.id === this.selectedDriver.id)) {
          drivers.unshift(this.selectedDriver)
        }
        this.driverState.list = drivers
      }).catch(error => {
        this.handleApiError(error, 'Error al obtener conductores')
      })
    } else {
      if (this.selectedDriver) {
        this.driverState.list = [this.selectedDriver]
      }
    }
    if (!this.isProcessing) {
      const gruaParams = {}
      gruaParams['expand'] = 'dealership'
      if (this.currentDealerId) {
        gruaParams['filter[dealer_id]'] = this.currentDealerId
      }
      GruaService.get(gruaParams).then(({ data }) => {
        let gruas = data.data || []
        if (this.selectedGrua && !gruas.some(d => d.id === this.selectedGrua.id)) {
          gruas.unshift(this.selectedGrua)
        }
        this.gruaState.list = gruas
      }).catch(error => {
        this.handleApiError(error, 'Error al obtener grúas')
      })
    } else {
      if (this.selectedGrua) {
        this.gruaState.list = [this.selectedGrua]
      }
    }
  },
  methods: {
    isEmptyObject (obj) {
      return !obj || Object.keys(obj).length === 0
    },
    close () {
      // Limpiar banderas de submit al cerrar el diálogo
      this.loading = false
      this.isSubmitting = false
      this.postCreateDeliverMode = false
      this.selectedTodos = []
      this.originalTodoIds = []
      this.$emit('close')
    },
    // Métodos para manejar actualizaciones con logs
    handleInitialLocationUpdate(value) {
      console.log('[TransportForm - handleInitialLocationUpdate]')
      console.log('  Nuevo valor recibido:', value)
      console.log('  Valor anterior:', this.initialLocation)
      console.log('  status.status:', this.status?.status)
      this.initialLocation = value
      console.log('  ➜ initialLocation actualizada a:', this.initialLocation)
    },
    handleFinalLocationUpdate(value) {
      this.finalLocation = value
    },
    // Verificar si el usuario tiene un driver asociado
    async checkUserDriver () {
      if (!this.currentUser || !this.currentUser.email) {
        return
      }
      
      this.checkingUserDriver = true
      this.userHasDriver = false
      
      try {
        const userDriverParams = { 'filter[email]': this.currentUser.email }
        // if (this.isCollaborator) {
          userDriverParams['expand'] = 'dealer'
        // }
        const response = await DriverService.get(userDriverParams)
        if (response.data && response.data.data && response.data.data.length > 0) {
          this.userHasDriver = true
          // Si encontramos un driver, lo seleccionamos automáticamente
          this.selectedDriver = response.data.data[0]
        } else {
          this.userHasDriver = false
        }
      } catch (error) {
        this.handleApiError(error, 'Error al verificar driver del usuario')
        this.userHasDriver = false
      } finally {
        this.checkingUserDriver = false
      }
    },
    // Signature pad methods
    initSignaturePad () {
      this.showSignature = true
      this.$nextTick(() => {
        const canvas = this.$refs.signatureCanvas
        if (canvas) {
          // Set canvas size
          canvas.width = 400
          canvas.height = 200
          
          this.signaturePad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5
          })
        }
      })
    },
    clearSignature () {
      if (this.signaturePad) {
        this.signaturePad.clear()
      }
    },
    saveSignature () {
      if (this.signaturePad && !this.signaturePad.isEmpty()) {
        this.signatureDataURL = this.signaturePad.toDataURL('image/png')
        this.showSignature = false
      }
    },
    editSignature () {
      this.showSignature = true
      this.$nextTick(() => {
        if (this.signaturePad && this.signatureDataURL) {
          this.signaturePad.fromDataURL(this.signatureDataURL)
        }
      })
    },
    // Customer signature methods for delivery (entrega)
    initCustomerSignaturePad () {
      this.showCustomerSignature = true
      this.$nextTick(() => {
        const canvas = this.$refs.customerSignatureCanvas
        if (canvas) {
          canvas.width = 400
          canvas.height = 200
          this.customerSignaturePad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5
          })
        }
      })
    },
    clearCustomerSignature () {
      if (this.customerSignaturePad) {
        this.customerSignaturePad.clear()
      }
    },
    saveCustomerSignature () {
      if (!this.customerSignaturePad) {
        console.error('customerSignaturePad is null or undefined')
        return
      }
      
      if (this.customerSignaturePad.isEmpty()) {
        console.warn('Signature pad is empty, nothing to save')
        this.$toast.warning('Por favor, firme antes de guardar')
        return
      }
      
      try {
        const dataURL = this.customerSignaturePad.toDataURL('image/png')
        
        if (dataURL && dataURL.length > 100) { // Verificar que no sea solo el header
          this.customerSignatureDataURL = dataURL
          this.showCustomerSignature = false
          this.$toast.success('Firma de entrega guardada correctamente')
        } else {
          console.error('Generated dataURL is too short or invalid')
          this.$toast.error('Error al generar la firma')
        }
      } catch (error) {
        console.error('Error generating signature dataURL:', error)
        this.$toast.error('Error al procesar la firma')
      }
    },
    editCustomerSignature () {
      this.showCustomerSignature = true
      this.$nextTick(() => {
        if (this.customerSignaturePad && this.customerSignatureDataURL) {
          this.customerSignaturePad.fromDataURL(this.customerSignatureDataURL)
        }
      })
    },
    // Customer signature methods for return (devolución)
    initCustomerSignatureReturnPad () {
      this.showCustomerSignatureReturn = true
      this.$nextTick(() => {
        const canvas = this.$refs.customerSignatureReturnCanvas
        if (canvas) {
          canvas.width = 400
          canvas.height = 200
          this.customerSignatureReturnPad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5
          })
        }
      })
    },
    clearCustomerSignatureReturn () {
      if (this.customerSignatureReturnPad) {
        this.customerSignatureReturnPad.clear()
      }
    },
    saveCustomerSignatureReturn () {
      if (this.customerSignatureReturnPad && !this.customerSignatureReturnPad.isEmpty()) {
        this.customerSignatureReturnDataURL = this.customerSignatureReturnPad.toDataURL('image/png')
        this.showCustomerSignatureReturn = false
      }
    },
    editCustomerSignatureReturn () {
      this.showCustomerSignatureReturn = true
      this.$nextTick(() => {
        if (this.customerSignatureReturnPad && this.customerSignatureReturnDataURL) {
          this.customerSignatureReturnPad.fromDataURL(this.customerSignatureReturnDataURL)
        }
      })
    },    
    // Helper method to convert base64 to blob (kept for compatibility but not used by signatures)
    dataURLToBlob (dataURL) {
      const arr = dataURL.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new Blob([u8arr], { type: mime })
    },
    // Helper method to convert signature to File (exactly like damage images)
    signatureToFile (signatureDataURL, filename) {
      try {
        if (!signatureDataURL) {
          console.error('No signature data provided')
          return null
        }
        
        // Split the data URL and decode
        const parts = signatureDataURL.split(',')
        if (parts.length !== 2) {
          console.error('Invalid data URL format')
          return null
        }
        
        const header = parts[0]
        const data = parts[1]
        
        // Extract mime type
        const mimeMatch = header.match(/data:([^;]+)/)
        const mimeType = mimeMatch ? mimeMatch[1] : 'image/png'
        
        // Convert base64 to binary
        let binaryString
        try {
          binaryString = atob(data)
        } catch (e) {
          console.error('Failed to decode base64:', e)
          return null
        }
        
        // Create Uint8Array
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }
        
        if (bytes.length === 0) {
          console.error('No binary data created')
          return null
        }
        
        // Create File object with same structure as damage images
        const file = new File([bytes], filename, { 
          type: mimeType,
          lastModified: Date.now()
        })
        
        return file
      } catch (error) {
        console.error('Error converting signature to file:', error)
        return null
      }
    },
    nextStep () {
      // Si es móvil, mostrar el diálogo de confirmación primero
      if (this.md.mobile()) {
        this.showMobileConfirmDialog = true
        this.mobileConfirmAction = 'nextStep'
      } else {
        this.currentStep = 4
      }
    },
    handleMobileConfirmation (confirmed) {
      this.showMobileConfirmDialog = false
      if (confirmed) {
        if (this.mobileConfirmAction === 'nextStep') {
          this.isSecure = true
          this.currentStep = 4
        } else if (this.mobileConfirmAction === 'submit') {
          this.isSecure = true
          this.currentStep = 5
        }
      }
      this.mobileConfirmAction = null
    },
    getLocationName (locationId) {
      if (!locationId) return 'No especificada'
      const location = this.locationOptions.find(loc => loc.id === locationId)
      return location ? location.displayName : 'No disponible'
    },
    handleImageChange (files) {
      if (!files || files.length === 0) return
      
      // Las imágenes ya vienen procesadas desde ImageManager, solo asignarlas directamente
      this.newDamageImages = files
      
      // Generar vistas previas para las nuevas imágenes
      this.newImagePreviews = []
      for (const file of files) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.newImagePreviews.push(e.target.result)
        }
        reader.readAsDataURL(file)
      }
      
      // Mostrar mensaje de éxito
      this.$toast.success(`${files.length} imagen(es) agregada(s). Total: ${this.savedImagesCount + files.length}/4`)
    },
    handleImageRemoved(imageId) {
      // Agregar ID de imagen a la lista de imágenes a eliminar
      if (!this.imagesToRemove.includes(imageId)) {
        this.imagesToRemove.push(imageId)
      }
    },
    async handleAdditionalImageRemoved(event) {
      if (event.type === 'saved' && event.imageId) {
        // Para imágenes guardadas, eliminar inmediatamente del servidor
        try {
          await MovimientoService.removeAdditionalImage(this.lastMovement.id, event.imageId)
          this.$toast.success('Imagen adicional eliminada')
          // Remover de la lista local de imágenes guardadas
          this.savedAdditionalImages = this.savedAdditionalImages.filter(img => img.id !== event.imageId)
        } catch (error) {
          this.handleApiError(error, 'Error al eliminar imagen adicional')
        }
      } else if (event.type === 'new') {
        // Para imágenes nuevas, solo remover de la lista local
        const updatedImages = [...this.newAdditionalImages]
        const updatedPreviews = [...this.newAdditionalImagePreviews]
        updatedImages.splice(event.index, 1)
        updatedPreviews.splice(event.index, 1)
        this.newAdditionalImages = updatedImages
        this.newAdditionalImagePreviews = updatedPreviews
      }
    },
    async handleAdditionalDocRemoved(event) {
      if (event.type === 'saved' && event.docId) {
        // Para documentos guardados, eliminar inmediatamente del servidor
        try {
          await MovimientoService.removeAdditionalDoc(this.lastMovement.id, event.docId)
          this.$toast.success('Documento adicional eliminado')
          // Remover de la lista local de documentos guardados
          this.savedAdditionalDocs = this.savedAdditionalDocs.filter(doc => doc.id !== event.docId)
        } catch (error) {
          this.handleApiError(error, 'Error al eliminar documento adicional')
        }
      } else if (event.type === 'new') {
        // Para documentos nuevos, solo remover de la lista local
        const updatedDocs = [...this.newAdditionalDocs]
        updatedDocs.splice(event.index, 1)
        this.newAdditionalDocs = updatedDocs
      }
    },
    async handleDamageDocRemoved(event) {
      if (event.type === 'saved' && event.docId) {
        // Para documentos de daños guardados, eliminar inmediatamente del servidor
        try {
          await MovimientoService.removeDamageDoc(this.lastMovement.id, event.docId)
          this.$toast.success('Documento de daño eliminado')
          // Remover de la lista local de documentos guardados
          this.savedDamageDocs = this.savedDamageDocs.filter(doc => doc.id !== event.docId)
        } catch (error) {
          this.handleApiError(error, 'Error al eliminar documento de daño')
        }
      } else if (event.type === 'new') {
        // Para documentos nuevos, solo remover de la lista local
        const updatedDocs = [...this.newDamageDocs]
        updatedDocs.splice(event.index, 1)
        this.newDamageDocs = updatedDocs
      }
    },
    async loadCompleteMovementData() {
      if (this.lastMovement && this.lastMovement.id && !this.dataLoaded) {
        this.dataLoaded = true // Marcar como cargado para evitar múltiples llamadas
        
        try {
          const response = await MovimientoService.view({ 
            id: this.lastMovement.id
          })
          const completeMovement = response.data.data
          
          // Debug: Ver qué campos vienen del backend
          console.log('🔍 Campos del backend:', {
            additional_info: completeMovement.additional_info,
            additional_info_reception: completeMovement.additional_info_reception,
            additional_info_delivery: completeMovement.additional_info_delivery
          })

          // Cargar información adicional según el tipo
          if (completeMovement.additional_info) {
            this.additionalInfo = completeMovement.additional_info
            console.log('✅ Cargado additionalInfo:', this.additionalInfo)
          }
          
          if (completeMovement.additional_info_reception) {
            this.additionalInfoReception = completeMovement.additional_info_reception
            console.log('✅ Cargado additionalInfoReception:', this.additionalInfoReception)
          }
          
          if (completeMovement.additional_info_delivery) {
            this.additionalInfoDelivery = completeMovement.additional_info_delivery
            this.showAdditionalInfo = true
            console.log('✅ Cargado additionalInfoDelivery:', this.additionalInfoDelivery)
          }
          
          if (completeMovement.observations) {
            this.damageNotes = completeMovement.observations
            console.log('✅ Cargado damageNotes desde observations:', this.damageNotes)
          }
          
          // Cargar imágenes adicionales si existen
          if (completeMovement.additionalImagesUrls && completeMovement.additionalImagesUrls.length > 0) {
            this.savedAdditionalImages = completeMovement.additionalImagesUrls
            this.showAdditionalInfo = true
          }
          
          // Cargar equipamientos desde equipment_status
          if (completeMovement.equipment_status && Array.isArray(completeMovement.equipment_status)) {
            this.loadEquipmentsFromMovement(completeMovement.equipment_status)
          }
        } catch (error) {
          this.handleApiError(error, 'Error al cargar información completa del movimiento')
          this.dataLoaded = false // Resetear en caso de error para permitir reintento
        }
      }
    },
    
    // Nuevo método para cargar equipamientos desde equipment_status del movimiento
    loadEquipmentsFromMovement(equipmentStatus) {
      // Resetear selectedEquipments
      this.selectedEquipments = {}
      
      // Cargar equipamientos desde equipment_status
      equipmentStatus.forEach(equipment => {
        this.selectedEquipments[equipment.equipment_name] = {
          selected: equipment.is_available,
          id: equipment.equipment_id
        }
        
        // También actualizar las props individuales para compatibilidad
        switch (equipment.equipment_name) {
          case 'baliza_v16':
            this.emergencyLight = equipment.is_available
            break
          case 'triangulo':
          case 'triangulo_emergencia':
            this.emergencyTriangle = equipment.is_available
            break
          case 'chalecos':
          case 'chaleco_alta_visibilidad':
            this.emergencyVest = equipment.is_available
            break
          case 'documentacion':
            this.documentation = equipment.is_available
            break
          case 'cable_carga':
            this.chargingCable = equipment.is_available
            break
        }
      })
      
      console.log('Equipamientos cargados desde movimiento:', this.selectedEquipments)
    },
    convertEndDateToTimestamp() {
      if (this.end_date) {
        this.end_date = Math.floor(new Date(this.end_date).getTime() / 1000)
      }
    },
    openImageDialog (imageUrl) {
      this.selectedImageUrl = imageUrl
      this.imageDialog = true
    },
    removeImage (index) {
      // Marcar para eliminar del servidor
      const imageId = this.lastMovement.imagesUrls[index].id
      this.imagesToRemove.push(imageId)
      // Crear copia actualizada del array
      const updatedImages = [...this.lastMovement.imagesUrls]
      updatedImages.splice(index, 1)
      this.lastMovement.imagesUrls = updatedImages
    },
    removeNewImage (index) {
      // Eliminar la vista previa
      this.newImagePreviews.splice(index, 1)
      // Eliminar el archivo
      const updatedFiles = [...this.newDamageImages]
      updatedFiles.splice(index, 1)
      this.newDamageImages = updatedFiles
    },
    
    // Función helper para convertir selectedEquipments al formato de la API
    async formatEquipmentsForAPI() {
      console.log('=== INICIO formatEquipmentsForAPI ===')
      console.log('selectedEquipments:', JSON.stringify(this.selectedEquipments, null, 2))
      console.log('Cantidad de equipamientos seleccionados:', Object.keys(this.selectedEquipments).length)
      
      let equipment = []
      
      // Si no hay equipamientos seleccionados, obtener todos los disponibles y marcarlos como false
      if (Object.keys(this.selectedEquipments).length === 0) {
        console.log('No hay equipamientos seleccionados, obteniendo lista completa del servicio...')
        try {
          // Obtener todos los equipamientos disponibles desde el servicio
          console.log('Llamando a EquipmentService.getConfigByCombinationMovements con:')
          console.log('  - status.status.id:', this.status.dealer.id)
          console.log('  - vehicleConditionComputed:', this.vehicleConditionComputed)
          console.log('  - tipo: movimiento')
          console.log('  - vehicleType:', this.vehicleType)
          
          const response = await EquipmentService.getConfigByCombinationMovements(
            this.status.dealer.id,
            this.vehicleConditionComputed,
            'movimiento',
            this.vehicleType
          )
          
          console.log('Respuesta del servicio de equipamientos:', response)
          
          if (response.data && response.data.data && response.data.data.data && response.data.data.data.equipments) {
            const availableEquipments = response.data.data.data.equipments.filter(eq => eq.is_active)
            console.log('Equipamientos activos encontrados:', availableEquipments.length)
            console.log('Lista de equipamientos:', availableEquipments.map(eq => ({ id: eq.id, name: eq.name })))
            
            // Enviar todos los equipamientos disponibles marcados como false
            availableEquipments.forEach(eq => {
              equipment.push({
                equipment_id: eq.id,
                is_available: 0 // Todos en false
              })
            })
            console.log('Equipamientos formateados (todos en false):', equipment)
          } else {
            console.log('No se encontraron equipamientos en la respuesta')
          }
        } catch (error) {
          console.error('ERROR al obtener equipamientos para API:', error)
          this.handleApiError(error, 'Error al obtener equipamientos para API')
          // Si falla, no enviar nada
          return { equipment: [] }
        }
      } else {
        // Si hay equipamientos seleccionados, usar la lógica original
        console.log('Hay equipamientos seleccionados, formateando para API...')
        Object.keys(this.selectedEquipments).forEach(equipmentName => {
          const equipmentData = this.selectedEquipments[equipmentName]
          console.log(`Procesando ${equipmentName}:`, equipmentData)
          if (equipmentData && equipmentData.id) {
            const formattedEquipment = {
              equipment_id: equipmentData.id,
              is_available: equipmentData.selected ? 1 : 0
            }
            console.log(`  -> Añadiendo: equipment_id=${formattedEquipment.equipment_id}, is_available=${formattedEquipment.is_available}`)
            equipment.push(formattedEquipment)
          } else {
            console.log(`  -> OMITIDO: No tiene ID o datos inválidos`)
          }
        })
      }
      
      console.log('Equipment final a enviar:', JSON.stringify(equipment, null, 2))
      console.log('=== FIN formatEquipmentsForAPI ===')
      return { equipment }
    },

      // Asignar los TO-DO seleccionados (opcional) al vehículo tras completar una recepción.
      // Usa assign-todos (reemplazo total), igual que VehiculoForm.
      async assignSelectedTodos (vehicleId) {
        if (!vehicleId) return
        const selectedIds = (this.selectedTodos || []).map(t => (t && t.id) || t)
        const originalIds = this.originalTodoIds || []
        // Si no hay cambios, no hacer nada
        if (JSON.stringify([...selectedIds].sort()) === JSON.stringify([...originalIds].sort())) return
        try {
          await VehiculoService.assignTodos(vehicleId, selectedIds)
          this.originalTodoIds = [...selectedIds]
        } catch (error) {
          console.error('Error al asignar TO-DO:', error)
          this.$toast.warning('La recepción se guardó, pero ocurrió un error al asignar los TO-DO')
        }
      },

      // Subir la documentación del vehículo (ficha técnica y permiso de circulación) tras la 1ª recepción.
      // Todos los archivos son opcionales.
      async uploadVehicleDocumentation (vehicleId) {
        if (!vehicleId) return
        const uploads = [
          { field: 'technical_sheet_front_image', param: '_technical_sheet_front_image', fn: VehiculoService.uploadTechnicalSheetFrontImage, label: 'ficha técnica (parte delantera)' },
          { field: 'technical_sheet_back_image', param: '_technical_sheet_back_image', fn: VehiculoService.uploadTechnicalSheetBackImage, label: 'ficha técnica (parte trasera)' },
          { field: 'circulation_permit_front_image', param: '_circulation_permit_front_image', fn: VehiculoService.uploadCirculationPermitFrontImage, label: 'permiso de circulación (parte delantera)' },
          { field: 'circulation_permit_back_image', param: '_circulation_permit_back_image', fn: VehiculoService.uploadCirculationPermitBackImage, label: 'permiso de circulación (parte trasera)' }
        ]
        for (const upload of uploads) {
          const file = this.vehicleDocFiles[upload.field]
          if (!file) continue
          const formData = new FormData()
          formData.append(upload.param, file)
          try {
            await upload.fn(vehicleId, formData)
          } catch (error) {
            console.error(`Error al subir documento de ${upload.label}:`, error)
            this.$toast.warning(`La recepción se guardó, pero hubo un problema al subir la imagen de ${upload.label}`)
          }
        }
      },

      // Precargar los TO-DO ya asignados al vehículo para que salgan seleccionados
      async initSelectedTodos () {
        let todos = (this.status && Array.isArray(this.status.todos)) ? this.status.todos : null
        const rawId = this.status && (this.status.id || this.status.vehicle_id)
        const vehicleId = typeof rawId === 'number' ? rawId : parseInt(String(rawId || '').replace(/_mov_.+$/, ''))
        if (!todos && vehicleId && !isNaN(vehicleId)) {
          try {
            const response = await VehiculoService.view({ id: vehicleId })
            todos = (response.data && response.data.data && response.data.data.todos) || []
          } catch (error) {
            console.error('Error al cargar TO-DO del vehículo:', error)
            todos = []
          }
        }
        const ids = (todos || []).map(t => (t && t.id) || t)
        this.selectedTodos = [...ids]
        this.originalTodoIds = [...ids]
      },

      async submit () {
      // PROTECCIÓN 1: Verificar si ya hay una petición en curso
      if (this.loading || this.isSubmitting) {
        console.warn(' Submit ignorado: Ya hay una operación en curso')
        this.$toast.warning('Ya hay una operación en proceso, por favor espere...')
        return
      }

      //  PROTECCIÓN 2: Debounce - evitar múltiples clics en menos de 2 segundos
      const now = Date.now()
      const timeSinceLastSubmit = now - this.lastSubmitTime
      if (timeSinceLastSubmit < 2000) {
        console.warn(`⚠️ Submit ignorado: Demasiado rápido (${timeSinceLastSubmit}ms desde el último)`)
        this.$toast.warning('Por favor espere antes de volver a intentar...')
        return
      }

      //  PROTECCIÓN 3: Establecer banderas INMEDIATAMENTE
      this.loading = true
      this.isSubmitting = true
      this.lastSubmitTime = now
      
      console.log(' Submit iniciado:', new Date().toISOString())

      try {
        // Siempre se guarda un movimiento tipo "move" con estado "unreported"
        // Solo se envía: el transporte (DRIVER/GRUA/CLIENTE), quién lo hace, la fecha,
        // la ubicación del paso 2 (destination_location_id) y la información adicional.
        const movimientoData = {
          type: this.movementType || 'move',
          status: 'unreported',
          transportation: this.transportMethod,
          driver_id: this.transportMethod !== 'GRUA' && this.selectedDriver ? this.selectedDriver.id : null,
          crane_id: this.transportMethod === 'GRUA' && this.selectedGrua ? this.selectedGrua.id : null,
          datetime: this.datetime,
          destination_location_id: this.destinationLocationId,
          source_location_id: this.initialLocation,
          // CO-13: fecha de devolución opcional del movimiento excepcional.
          end_date: this.end_date || null,
          // CO-01/CO-02: en ubicaciones compartidas el backend necesita el
          // dealer receptor explícito (el seleccionado en el paso 1) para
          // atribuirle el vehículo cedido; si no lo mandamos, cae en el
          // primary de la ubicación y rompe los filtros.
          destination_dealer_id: this.destinationDealerId,
          additional_info: this.additionalInfo && this.additionalInfo.trim() !== '' ? this.additionalInfo : null,
          vehicle_id: (typeof this.status.id === 'number' && !isNaN(this.status.id)) ? this.status.id : (this.vehicleIdNumber || parseInt(this.status.id.toString().replace(/_mov_.+$/, ''))),
          responsible_id: this.currentUser.id,
          dealer_id: this.isCollaborator ? this.currentDealerId : null,
          is_excepcional: 1 // Siempre marcar como excepcional para que no afecte el flujo normal
        }
        console.log('movimientoData a enviar:', movimientoData)
        let response = await MovimientoService.post(movimientoData)
        const { error } = response

        if (error) {
          this.$toast.error('Ocurrió un error al procesar el movimiento')
          return
        }

        this.$toast.success('Movimiento iniciado con éxito')
        this.$emit('submit', movimientoData)
        if (!this.noRedirect) this.$router.push({ name: 'Movimiento' })
      } catch (error) {
        this.handleApiError(error, 'Error al procesar el movimiento')
      } finally {
        // 🔒 PROTECCIÓN 4: Limpiar banderas en el finally para garantizar que siempre se ejecute
        this.loading = false
        this.isSubmitting = false
        this.isDelivering = false
        console.log('✅ Submit finalizado:', new Date().toISOString())
      }
    },
    handleSubmitDeliver () {
      this.isDelivering = true
      this.submit()
    },
    checkSalesDirectProcessing() {
      // Verificar todas las condiciones requeridas
      const isAssignmentType = this.movementType === 'assignment'

      // Verificar si las fechas son iguales (inicio y devolución)
      let isSameDate = false
      
      if (this.datetime && this.end_date) {
        const toDate = (val) => {
          const n = Number(val)
          // if value looks like seconds (<= 10-11 digits) convert to ms, otherwise assume ms or date string
          return isNaN(n) ? new Date(val) : (n < 1e12 ? new Date(n * 1000) : new Date(n))
        }
        const startDate = toDate(this.datetime)
        const endDate = toDate(this.end_date)
        startDate.setHours(0, 0, 0, 0)
        endDate.setHours(0, 0, 0, 0)
        isSameDate = startDate.getTime() === endDate.getTime()
      }

      // Verificar si la ubicación inicial y final son la misma
      const isSameLocation =
        this.initialLocation != null &&
        this.finalLocation != null &&
        this.initialLocation === this.finalLocation

      // Retornar true solo si todas las condiciones se cumplen
      return isAssignmentType && isSameDate && isSameLocation
    },
    filterDrivers (item, queryText, itemText) {
      const text = `${item.name} ${item.lastname} ${item.document_number}`.toLowerCase()
      const searchText = queryText.toLowerCase()
      return text.includes(searchText)
    },
    displayText (item) {
      const driverInfo = `${item.name} ${item.lastname}`
      
      // Solo mostrar información del dealer si el usuario es colaborador
      let dealerInfo = ''
        if (item.dealer && item.dealer.name) {
          dealerInfo = ` (${item.dealer.name})`
        } else if (item.dealer && typeof item.dealer === 'string') {
          dealerInfo = ` (${item.dealer})`
        } else if (item.dealer_name) {
          dealerInfo = ` (${item.dealer_name})`
        }
      return driverInfo + dealerInfo + '-' + item.document_number
    },
    driverDebouncedSearch: _.debounce(function () {
      if (this.driverState.search.length > 2) {
        this.driverState.loading = true
        const driverSearchParams = { 'filter[document_number]': this.driverState.search }
          driverSearchParams['expand'] = 'dealer'
          // Si tenemos dealer_id del movimiento/vehículo, filtrar por ese dealer
          if (this.currentDealerId) {
            driverSearchParams['filter[dealer_id]'] = this.currentDealerId
          }
        DriverService.get(driverSearchParams).then(({ data }) => {
          let drivers = data.data || []
          
          // Si tenemos dealer_id y es colaborador, filtrar manualmente
          // if (this.currentDealerId && this.isCollaborator) {
          //   drivers = drivers.filter(driver => 
          //     driver.dealer && driver.dealer.id === this.currentDealerId
          //   )
          //   console.log('Driver search filtrados por dealer:', drivers.length)
          // }
          
          this.driverState.list = drivers
          this.driverState.loading = false
        }).catch(error => {
          this.handleApiError(error, 'Error al buscar conductores')
          this.driverState.loading = false
          this.showNoDriversMessage = true
        })
      } else {
        this.driverState.list = []
        this.showNoDriversMessage = false
      }
      this.showNoDriversMessage = this.driverState.list.length === 0 && this.driverState.search.length > 0
    }, 300),
    openCreateDriverDialog () {
      this.createDriverDialog = true
    },
    // Método centralizado para cargar ubicaciones
  async loadLocationOptions() {
  try {
    const hasPartners = this.$store.getters.IS_PARTNER

    let locations = []

    if (hasPartners) {
      const response = await LocationService.getPartnerLocations()
      // La respuesta viene como response.data.data.locations (hay un data anidado)
      locations = response.data?.data?.locations || []
    } else {
      const response = await LocationService.get({ 'per-page': 1000, expand: 'dealer,dealers' })
      locations = response.data?.data || []
    }

    const ownDealer = this.$store.getters.CURRENT_USER && this.$store.getters.CURRENT_USER.dealer

    this.locationOptions = locations.map(location => {
      const displayName = locationDisplayName(location, ownDealer)

      return {
        id: location.id,
        name: location.name,
        displayName: displayName,
        dealer_id: location.dealer_id,
        is_own: location.is_own !== undefined ? location.is_own : true
      }
    })
  } catch (error) {
    this.handleApiError(error, 'Error al obtener ubicaciones')
  }
},
    // Cargar los dealers que pertenecen al grupo del dealer/usuario actual
    async loadGroupDealers() {
      this.groupDealersLoading = true
      try {
        let currentDealerId = this.currentUser && this.currentUser.dealer ? this.currentUser.dealer.id : null
        if (!currentDealerId) {
          currentDealerId = this.currentDealerId
        }
        // CO-11: el que no puede ser destino de un movimiento excepcional es
        // el dealer que tiene el vehículo en ese momento (holder), no el
        // usuario logueado. El propietario (si no lo tiene) sí puede ser
        // destino (puede recuperarlo).
        const vehicleOwnerId = (this.status && this.status.dealer_id) ||
          (this.status && this.status.dealer && this.status.dealer.id) || null
        const holderDealerId = this.resolveVehicleHolder() || vehicleOwnerId || currentDealerId
        const response = await GrupoService.index({ expand: 'dealerships,dealerships.partners' })
        const groups = (response.data && response.data.data) || []
        const dealers = []
        groups.forEach(group => {
          const groupDealers = group.dealerships || []
          const isMember = currentDealerId && groupDealers.some(d => d.id === currentDealerId)
          if (isMember) {
            // N-11: entre partners no tiene sentido un excepcional (ya comparten
            // stock): se excluye al que tiene el vehículo y a sus partners. El
            // propietario siempre sale, para poder recuperar su vehículo.
            const holder = groupDealers.find(d => d.id === holderDealerId)
            const excluded = [holderDealerId, ...((holder && holder.partners) || []).map(p => p.id)]
              .filter(id => id === holderDealerId || id !== vehicleOwnerId)
            groupDealers.forEach(d => {
              if (!excluded.includes(d.id) && !dealers.some(x => x.id === d.id)) {
                dealers.push({ id: d.id, name: d.name })
              }
            })
          }
        })
        this.destinationDealers = dealers
      } catch (error) {
        this.handleApiError(error, 'Error al cargar los dealers del grupo')
      } finally {
        this.groupDealersLoading = false
      }
    },
    // Devuelve el dealer que tiene el vehículo en este momento, recorriendo
    // los movimientos excepcionales por fecha ascendente. Si el flag
    // `exceptional_movement` es false o no hay excepcionales, el holder es el
    // propietario del vehículo.
    resolveVehicleHolder() {
      const vehicle = this.status
      if (!vehicle) return null
      const ownerId = vehicle.dealer_id || (vehicle.dealer && vehicle.dealer.id) || null
      if (!vehicle.exceptional_movement) {
        return ownerId
      }
      const movements = (vehicle.movements || []).slice().sort((a, b) => (a.datetime || 0) - (b.datetime || 0))
      let holder = ownerId
      movements.forEach(m => {
        if (!m.is_excepcional) return
        const dest = m.destination_dealer_id ||
          (m.destinationLocation && m.destinationLocation.dealer_id) ||
          (m.destination_location && m.destination_location.dealer_id) ||
          null
        if (dest) {
          holder = dest
        }
      })
      return holder
    },
    // Carga las ubicaciones del dealer de destino seleccionado
    async loadDestinationLocations(dealerId) {
      this.destinationLocations = []
      this.destinationLocationId = null
      if (!dealerId) {
        return
      }
      this.destinationLocationsLoading = true
      try {
        const params = {
          'id': dealerId,
          expand: 'dealer',
          'per-page': 100
        }
        const response = await LocationService.getLocationDealer(params)
        // El endpoint responde con { data: { dealer_id, dealer_name, locations: [...] } }
        let locations = []
        if (response.data && response.data.data && Array.isArray(response.data.data.locations)) {
          locations = response.data.data.locations
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          locations = response.data.data
        }
        this.destinationLocations = locations.map(location => ({
          id: location.id,
          name: location.name,
          displayName: location.dealer_name ? `${location.name} (${location.dealer_name})` : location.name
        }))
      } catch (error) {
        this.handleApiError(error, 'Error al cargar las ubicaciones del dealer')
      } finally {
        this.destinationLocationsLoading = false
      }
    },
    handleDriverCreated (event) {
      this.showNoDriversMessage = false
      this.createDriverDialog = false
      const newDriver = event.value.data
      this.selectedDriver = newDriver
      this.driverState.list.push(newDriver)
      this.driverState.search = ''
      
      // Si el nuevo driver tiene el mismo email que el usuario autenticado, actualizar userHasDriver
      if (this.currentUser && this.currentUser.email && newDriver.email === this.currentUser.email) {
        this.userHasDriver = true
      }
    },
    loadGruas () {
      this.gruaState.loading = true
      if (!this.isProcessing && this.gruaState.list.length > 0) {
        this.gruaState.loading = false
        return
      }
      
      // Solo cargar y preseleccionar la grúa del último movimiento si estamos COMPLETANDO ese movimiento
      if (this.isCompletingMovement && this.lastMovement.crane_id) {
        const gruaParams = {
          id: this.lastMovement.crane_id,
          expand: 'dealership'
        }
        
        GruaService.view(gruaParams)
          .then(({ data }) => {
            const grua = data.data
            if (grua) {
              this.gruaState.list = [grua]
              this.selectedGrua = grua
            }
            this.gruaState.loading = false
          })
          .catch((error) => {
            console.error('Error al cargar grúa específica:', error)
            this.gruaState.list = []
            this.gruaState.loading = false
          })
      } else {
        // Cargar lista de grúas disponibles sin preseleccionar ninguna
        const gruaParams = {
          expand: 'dealership'
        }
        
        // Si tenemos dealer_id del movimiento, agregarlo al filtro
        if (this.currentDealerId) {
          gruaParams['filter[dealer_id]'] = this.currentDealerId
        }
        
        GruaService.get(gruaParams)
          .then(({ data }) => {
            let gruas = data.data || []
            
            // Si tenemos dealer_id y es colaborador, filtrar manualmente
            if (this.currentDealerId && this.isCollaborator) {
              gruas = gruas.filter(grua =>
                grua.dealership && grua.dealership.id === this.currentDealerId
              )
            }
            
            this.gruaState.list = gruas
            this.gruaState.loading = false
          })
          .catch((error) => {
            console.error('Error al cargar lista de grúas:', error)
            this.gruaState.list = []
            this.gruaState.loading = false
          })
      }
    },
    filterGruas (item, queryText, itemText) {
      const text = `${item.company} ${item.cif || ''} ${item.phone || ''}`.toLowerCase()
      const searchText = queryText.toLowerCase()
      return text.includes(searchText)
    },
    displayGrua (item) {
      const gruaInfo = item.company || ''
      
      // Solo mostrar información del dealer si el usuario es colaborador
      let dealerInfo = ''
      // if (this.isCollaborator) {
        if (item.dealership && item.dealership.name) {
          dealerInfo = ` (${item.dealership.name})`
        } else if (item.dealer && item.dealer.name) {
          dealerInfo = ` (${item.dealer.name})`
        } else if (item.dealer && typeof item.dealer === 'string') {
          dealerInfo = ` (${item.dealer})`
        } else if (item.dealer_name) {
          dealerInfo = ` (${item.dealer_name})`
        }
      // }
      
      return gruaInfo + dealerInfo
    },
    gruaDebouncedSearch: _.debounce(function () {
      if (this.gruaState.search.length > 2) {
        this.gruaState.loading = true
        const gruaSearchParams = { 'filter[company]': this.gruaState.search }
        // if (this.isCollaborator) {
          gruaSearchParams['expand'] = 'dealer'
          // Si tenemos dealer_id del movimiento/vehículo, filtrar por ese dealer
          if (this.currentDealerId) {
            gruaSearchParams['filter[dealer_id]'] = this.currentDealerId
          }
        // }
        GruaService.get(gruaSearchParams)
          .then(({ data }) => {
            let gruas = data.data || []
            
            // Si tenemos dealer_id y es colaborador, filtrar manualmente
            if (this.currentDealerId && this.isCollaborator) {
              gruas = gruas.filter(grua => 
                (grua.dealership && grua.dealership.id === this.currentDealerId) ||
                (grua.dealer && grua.dealer.id === this.currentDealerId)
              )
            }
            
            this.gruaState.list = gruas
            this.gruaState.loading = false
          })
          .catch(error => {
            this.handleApiError(error, 'Error al buscar grúas')
            this.gruaState.loading = false
          })
      }
    }, 300),
    openCreateGruaDialog () {
      this.createGruaDialog = true
    },
    handleGruaCreated (event) {
      this.createGruaDialog = false
      const newGrua = event.value.data
      this.selectedGrua = newGrua
      this.gruaState.list.push(newGrua)
      this.gruaState.search = ''
    },
    async handleMovimientoEntregado (movimiento) {
      console.log('Movimiento entregado recibido:', movimiento)
      
      try {
        // Mostrar toast de éxito
        this.$toast.success('Cesión creada con éxito. Cargando formulario de entrega...')
        
        // ACTUALIZAR el lastMovement INMEDIATAMENTE con el movimiento recién creado
        console.log('🔄 Actualizando lastMovement con el movimiento recién creado')
        this.lastMovement = movimiento
        
        // Recargar el vehículo para obtener el movimiento actualizado
        const response = await VehiculoService.view({ id: this.status.id })
        if (response && response.data && response.data.data) {
          const vehiculoActualizado = response.data.data
          
          // Emitir evento al componente padre para que actualice y reabra el formulario
          this.$emit('reload-with-movement', {
            vehicle: vehiculoActualizado,
            movementId: movimiento.id
          })
          
          console.log('Evento reload-with-movement emitido con movimiento:', movimiento.id)
        }
      } catch (error) {
        console.error('Error al cargar el movimiento:', error)
        this.$toast.error('Error al cargar el formulario de entrega')
      }
    }
  },
  watch: {
    destinationDealerId: function (val) {
      this.loadDestinationLocations(val)
      // CO-13: en el flujo excepcional (único que usa el paso de "Empresa
      // de destino"), fijamos movementType = 'move' para que el paso 3
      // muestre la fecha de devolución, igual que en los movimientos
      // normales. Hasta ahora quedaba vacío y el picker no se dibujaba.
      if (val && !this.isAssignment && !this.isMovement) {
        this.movementType = 'move'
      }
    },
    // Añade estos watchers si no existen ya
    'gruaState.search': function (val) {
      this.gruaDebouncedSearch()
    },
    transportMethod: function (val) {
      // Si se selecciona 'GRUA', cargar la lista de grúas
      if (val === 'GRUA') {
        this.loadGruas()
      }
      // Si se selecciona 'YO MISMO' en un movimiento con estado 'reported', verificar driver del usuario
      if (val === 'YO MISMO' && this.lastMovement && this.lastMovement.status === 'reported') {
        this.checkUserDriver()
      }
    },
    lastMovement: {
      handler(newMovement, oldMovement) {
        // Solo cargar si es un nuevo movimiento (cambió el ID) y estamos procesando
        if (newMovement && 
            Object.keys(newMovement).length > 0 && 
            this.isProcessing &&
            newMovement.id &&
            (!oldMovement || oldMovement.id !== newMovement.id) &&
            !newMovement.additional_info && // Solo cargar si no tiene ya la info adicional
            !this.dataLoaded) {
          this.loadCompleteMovementData()
        }
        // Propagar end_date del movimiento al estado local para que componentes hijos lo reciban
        if ((newMovement && (newMovement.end_date || newMovement.end_date === 0) && !this.isAssignment && !this.isMovement) &&
            (!oldMovement || oldMovement.end_date !== newMovement.end_date)) {
          this.end_date = newMovement.end_date
        }
        
        // Inicializar franchise y km_limit con valores predeterminados o guardados
        if (newMovement && newMovement.type === 'assignment' && newMovement.status === 'reported') {
          // Si el movimiento ya tiene valores guardados, usarlos
          if (newMovement.franchise !== undefined && newMovement.franchise !== null) {
            this.franchise = newMovement.franchise
          } else if (this.defaultFranchiseIndex !== null && this.dealerFranchises[this.defaultFranchiseIndex]) {
            // Si no, usar el valor predeterminado del dealer
            this.franchise = this.dealerFranchises[this.defaultFranchiseIndex]
          }
          // Inicializar franchise y km_limit para status 'processing' (leer valores guardados del movimiento)
          if (newMovement && newMovement.type === 'assignment' && newMovement.status === 'processing') {
            if (newMovement.franchise !== undefined && newMovement.franchise !== null) {
              this.franchise = newMovement.franchise
            }
            if (newMovement.km_limit !== undefined && newMovement.km_limit !== null) {
              this.km_limit = newMovement.km_limit
            }
          }
          
          if (newMovement.km_limit !== undefined && newMovement.km_limit !== null) {
            this.km_limit = newMovement.km_limit
            console.log('  ✓ km_limit seteado desde movimiento:', this.km_limit)
          } else if (this.defaultKmLimitIndex !== null && this.dealerKmLimits[this.defaultKmLimitIndex]) {
            // Si no, usar el valor predeterminado del dealer
            this.km_limit = this.dealerKmLimits[this.defaultKmLimitIndex]
            console.log('  ✓ km_limit seteado desde default:', this.km_limit)
          }
        }
      },
      deep: false, // Cambiar a false para evitar bucles
      immediate: true
    },
    status: {
      handler(newStatus, oldStatus) {
        // Resetear la bandera cuando cambie el vehículo
        if (newStatus && (!oldStatus || oldStatus.id !== newStatus.id)) {
          this.dataLoaded = false
          // Precargar los TO-DO ya asignados al vehículo
          this.initSelectedTodos()
        }
      },
      immediate: true
    },
    additionalInfo: {
      handler(newVal, oldVal) {
      },
      immediate: true
    },
    selectedEquipments: {
      handler(newEquipments) {
        // Emitir evento al padre si es necesario
        this.$emit('equipments-changed', newEquipments)
      },
      deep: true,
      immediate: true
    },
    currentUser: {
      handler(newUser) {
        // Si el usuario llega después de created, cargar los dealers del grupo
        if (newUser && newUser.dealer && this.destinationDealers.length === 0 && !this.groupDealersLoading) {
          this.loadGroupDealers()
        }
        // Cuando se carga el usuario, inicializar franchise y km_limit si aplica
        if (newUser && this.lastMovement && this.lastMovement.type === 'assignment' && (this.lastMovement.status === 'reported' || this.lastMovement.status === 'processing')) {
          // Solo inicializar si aún no tienen valor
          if (this.lastMovement.status === 'processing') {
    // En processing siempre leer del movimiento, no de defaults
            if (this.franchise === null && this.lastMovement.franchise != null) {
              this.franchise = this.lastMovement.franchise
            }
            if (this.km_limit === null && this.lastMovement.km_limit != null) {
              this.km_limit = this.lastMovement.km_limit
            }
          } else {
          if (this.franchise === null) {
            if (this.lastMovement.franchise !== undefined && this.lastMovement.franchise !== null) {
              this.franchise = this.lastMovement.franchise
              console.log('  ✓ franchise inicializada desde movimiento:', this.franchise)
            } else if (this.defaultFranchiseIndex !== null && this.dealerFranchises[this.defaultFranchiseIndex]) {
              this.franchise = this.dealerFranchises[this.defaultFranchiseIndex]
              console.log('  ✓ franchise inicializada desde default:', this.franchise)
            }
          }
          
          if (this.km_limit === null) {
            if (this.lastMovement.km_limit !== undefined && this.lastMovement.km_limit !== null) {
              this.km_limit = this.lastMovement.km_limit
              console.log('  ✓ km_limit inicializado desde movimiento:', this.km_limit)
            } else if (this.defaultKmLimitIndex !== null && this.dealerKmLimits[this.defaultKmLimitIndex]) {
              this.km_limit = this.dealerKmLimits[this.defaultKmLimitIndex]
              console.log('  ✓ km_limit inicializado desde default:', this.km_limit)
            }
          }
        }
      }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.transport-form-card {
  border-radius: 8px;
  overflow: hidden;
}

.v-stepper {
  box-shadow: none;
}

.equipment-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.transition-swing {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.v-radio {
  margin-top: 0;
}

.v-radio label {
  font-weight: 500;
}
.vehicle-info-card, .vehicle-condition-card, .damage-details-card {
  height: 100%;
  border-radius: 8px;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.vehicle-condition-group .v-input__slot {
  margin-bottom: 0;
}

.vehicle-condition-radio {
  display: flex;
  justify-content: center;
  margin: 8px 0 0 0;
}

.vehicle-condition-radio .v-input--selection-controls__input {
  margin-right: 0;
}

.v-slide-y-transition-enter-active,
.v-slide-y-transition-leave-active {
  transition: all 0.4s ease-in-out;
}
.disabled-card {
  opacity: 0.7;
  cursor: default !important;
  pointer-events: none;
}
/* Nuevos estilos para el manejo de imágenes */
.image-preview-card, .new-image-preview-card {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.image-preview {
  transition: transform 0.3s ease;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-preview-card:hover .image-overlay,
.new-image-preview-card:hover .image-overlay {
  opacity: 1;
}

.image-preview-card:hover .image-preview,
.new-image-preview-card:hover .image-preview {
  transform: scale(1.05);
}

.image-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;
}

.image-dialog-content {
  max-height: 80vh;
  background-color: #000;
}

.saved-images-container, .new-images-container {
  transition: all 0.3s ease;
}

/* Signature pad styles */
.signature-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.signature-wrapper {
  display: flex;
  justify-content: center;
  margin: 16px 0;
}

.signature-canvas {
  border: 2px solid #ccc;
  border-radius: 4px;
  background-color: white;
  cursor: crosshair;
}

.signature-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.signature-image {
  max-width: 400px;
  max-height: 200px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

@media (max-width: 600px) {
  .signature-canvas {
    width: 300px;
    height: 150px;
  }
  
  .signature-image {
    max-width: 300px;
    max-height: 150px;
  }
}
</style>
